import { objectSpread2 as _objectSpread2 } from '../_virtual/_rollupPluginBabelHelpers.js';
import * as React from 'react';
import { visibility } from './constants.js';

/**
 * Department object, response of [webWidget:get chat:department]{@link https://developer.zendesk.com/embeddables/docs/widget/chat#get-chatdepartment}
 * @typedef {Object} DepartmentObject
 * @param {number} id
 * @param {number} name
 * @param {'online'|'away'|'offline'} status
 */

/**
 * Department object with replaced status.
 * @typedef {Object} LocalDepartmentObject
 * @param {number} id
 * @param {number} name
 * @param {'online'|'offline'} status
 */

/**
 * Helper function for converting department.status to one of "online" or
 * "offline" for easier comparison, since we dont care about the away status and
 * the zopim livechat api accepts only these 2 values.
 * @param {DepartmentObject} department
 * @returns {LocalDepartmentObject}
 */

function convertStatus() {
  var _department$status;

  var department = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return _objectSpread2(_objectSpread2({}, department), {}, {
    status: (_department$status = department.status) === null || _department$status === void 0 ? void 0 : _department$status.replace("away", "offline")
  });
}
/**
 * Helper function for checking status of primary & fallback department
 * @param {LocalDepartmentObject} primary
 * @param {LocalDepartmentObject} fallback
 */

function compareDepartments(primary, fallback) {
  var status = primary.status === "online" ? "online" : fallback.status;
  var selectDepartmentId = primary.status === "online" ? primary.id : fallback.id;
  return {
    status,
    selectDepartmentId
  };
}
/**
 * Utility function which decides to which extent the zendesk chat UI is visible,
 * by comparing several state values.
 *
 * NOTE: When "Chat Create" chatbot is active we should hide zendesk.
 * Chatbot sets the window display to 'none' when it hands over the
 * conversation to Zendesk
 * @param {boolean} showWidget state.showWidget
 * @param {boolean} launcherEligible state.zendesk.launcherEligible
 * @param {boolean} forceOpen state.forceOpenOnUnreadMsgs
 * @param {element} chatbot state.chatbot DOM element added by ChatCreate
 * @param {boolean} disabled state.disabled
 * @returns {("hide"|"launcher"|"widget")} decision of value to be updated as
 * state.zendesk.visibility
 */

function compareVisibility(showWidget, launcherEligible, forceOpen, chatbot, disabled) {
  var noLauncher = !showWidget && !launcherEligible && !forceOpen;
  var launcher = !showWidget && launcherEligible;
  var chatbotActive = showWidget && chatbot.element && chatbot.element.style.display !== "none";

  if (disabled || noLauncher || chatbotActive) {
    return visibility.HIDE;
  } else if (launcher) {
    return visibility.LAUNCHER;
  } else if (showWidget && !chatbotActive) {
    return visibility.WIDGET;
  }
}
function usePolling(callback, _ref) {
  var {
    deps = [],
    condition = true,
    interval = 250
  } = _ref;

  /* istanbul ignore next */
  if (typeof window === undefined) {
    return;
  }

  React.useEffect(() => {
    if (condition) {
      var timerId = window.setInterval(() => {
        callback();
      }, interval);
      return () => {
        clearInterval(timerId);
      };
    }
  }, [...deps]);
}

export { compareDepartments, compareVisibility, convertStatus, usePolling };
